import React, { useState, useRef, useEffect } from 'react';
import '../styles/MultiSelectDropdown.css';

interface MultiSelectDropdownProps {
  options: string[];
  value: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select options',
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionClick = (option: string) => {
    if (value.includes(option)) {
      onChange(value.filter(item => item !== option));
    } else {
      onChange([...value, option]);
    }
  };

  const handleSelectAll = () => {
    if (value.length === options.length) {
      onChange([]);
    } else {
      onChange([...options]);
    }
  };

  const getDisplayText = () => {
    if (value.length === 0) return placeholder;
    if (value.length === 1) return value[0];
    if (value.length === options.length) return 'All selected';
    if (value.length <= 2) {
      return value.join(', ');
    }
    return `${value.slice(0, 2).join(', ')} + ${value.length - 2} more`;
  };

  return (
    <div className={`multiselect-dropdown ${disabled ? 'disabled' : ''}`} ref={dropdownRef}>
      <div className="multiselect-dropdown-toggle" onClick={handleToggle} style={{ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}>
        <span className="multiselect-dropdown-text">{getDisplayText()}</span>
        <span className="multiselect-dropdown-arrow">▼</span>
      </div>
      
      {isOpen && (
        <div className="multiselect-dropdown-menu">
          <div className="multiselect-option select-all" onClick={handleSelectAll}>
            <input
              type="checkbox"
              checked={value.length === options.length}
              onChange={() => {}}
              className="multiselect-checkbox"
            />
            <span>Select All</span>
          </div>
          <div className="multiselect-divider"></div>
          {options.map(option => (
            <div
              key={option}
              className="multiselect-option"
              onClick={() => handleOptionClick(option)}
            >
              <input
                type="checkbox"
                checked={value.includes(option)}
                onChange={() => {}}
                className="multiselect-checkbox"
              />
              <span>{option}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;